/* Cart edits use exact lines, and reconcile every write before advancing.
   A private marker is a receipt, not an idempotency key: never repeat add.js. */
(() => {
  const photoKeys = ['Pet photo', 'Second pet photo', 'Third pet photo', 'Reference photo'];
  const editFields = ['_pet_edit_pending', '_pet_edit_source', '_pet_edit_fingerprint', '_pet_edit_target_variant', '_pet_edit_target_quantity', '_pet_edit_target_pet_count'];
  const message = (component, key) => component.dataset[key] || '';
  const propertyName = input => /^properties\[(.+)\]$/.exec(input.name)?.[1];
  const signature = async item => {
    const data = JSON.stringify([String(item.variant_id || item.id), item.quantity, Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b))]);
    const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(data));
    return Array.from(new Uint8Array(bytes), n => n.toString(16).padStart(2, '0')).join('');
  };
  const safePhoto = value => {
    if (typeof value !== 'string') return false;
    const url = new URL(value, location.origin);
    return (url.origin === location.origin && /^\/preview-files\/[\w-]+$/.test(url.pathname)) ||
      (url.protocol === 'https:' && url.hostname === 'cdn.shopify.com' && url.pathname.includes('/uploads/'));
  };

  class PetCartEditor {
    constructor(component) {
      this.component = component;
      this.form = component.form;
      this.fields = component.querySelector('.pet-customization__fields');
      this.notice = component.querySelector('[data-pet-edit-notice]');
      this.recover = component.querySelector('[data-pet-edit-recover]');
      this.keep = component.querySelector('[data-pet-edit-keep]');
      this.discard = component.querySelector('[data-pet-edit-discard]');
      this.key = new URL(location.href).searchParams.get('pr_edit');
      this.cartUrl = component.dataset.cartUrl || '/cart';
      this.storageKey = `pet-reverie-edit:${this.key}`;
      this.ready = false;
      component.cartEditor = this;
      component.querySelector('[data-pet-edit-banner]').hidden = false;
      component.button.dataset.readyLabel = message(component, 'editReadyLabel');
      component.button.dataset.busyLabel = message(component, 'editBusyLabel');
      this.recover.addEventListener('click', () => this.check());
      this.keep.addEventListener('click', () => this.check(true));
      this.discard.addEventListener('click', () => this.discardChanges());
      try { this.record = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null'); } catch { this.record = null; }
      this.load();
    }
    persist() {
      try {
        if (this.record) sessionStorage.setItem(this.storageKey, JSON.stringify(this.record));
        else sessionStorage.removeItem(this.storageKey);
      } catch { /* Current-page recovery still works without browser storage. */ }
    }
    async cart() {
      const response = await fetch(`${this.cartUrl}.js`, { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(message(this.component, 'editCartCheckFailed'));
      const cart = await response.json();
      if (!Array.isArray(cart.items)) throw new Error(message(this.component, 'editCartCheckFailed'));
      return cart;
    }
    async write(endpoint, body) {
      const multipart = body instanceof FormData;
      return fetch(`${this.cartUrl}/${endpoint}.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: multipart ? { Accept: 'application/json' } : { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: multipart ? body : JSON.stringify(body)
      });
    }
    show(message, { ready = false, recover = false, keep = false, discard = false } = {}) {
      this.ready = ready;
      this.fields.disabled = !ready || this.component.dataset.orderingAllowed !== 'true';
      this.component.resetSubmission();
      this.notice.textContent = message;
      this.recover.hidden = !recover;
      this.recover.disabled = false;
      this.keep.hidden = !keep; this.keep.disabled = false;
      this.discard.hidden = !discard; this.discard.disabled = false;
    }
    busy(message) {
      this.ready = false;
      this.fields.disabled = true;
      this.recover.disabled = true;
      this.keep.disabled = true; this.discard.disabled = true;
      this.component.button.disabled = true;
      this.form.setAttribute('aria-busy', 'true');
      this.notice.textContent = message;
    }
    fill(item) {
      const component = this.component;
      const variant = String(item.variant_id || item.id);
      const option = Array.from(component.variant.options).find(option => option.value === variant);
      if (String(item.product_id) !== component.dataset.productId || !option || option.dataset.petProfile !== 'portrait') {
        throw new Error(message(this.component, 'editCannotEdit'));
      }
      component.variant.value = variant;
      const countChoice = component.querySelector('select[data-pet-count-choice]');
      if (countChoice && option.dataset.petCountChoice === 'true') {
        const savedCount = String(item.properties?.['Pet count'] || '');
        countChoice.value = ['1', '2'].includes(savedCount) ? savedCount : '1';
      }
      const photoSubmission = item.properties?.['Photo submission'] === 'Send after checkout' ? 'Send after checkout' : 'Upload now';
      component.querySelectorAll('[data-pet-photo-submission]').forEach(input => { input.checked = input.value === photoSubmission; });
      const customisationId = component.querySelector('[name="properties[_customisation_id]"]');
      if (customisationId) customisationId.value = item.properties?._customisation_id || customisationId.value || crypto.randomUUID();
      component.querySelector('[data-pet-quantity]').value = item.quantity;
      component.petNames.forEach(input => { input.value = item.properties?.[propertyName(input)] || ''; });
      const notes = component.querySelector('[data-pet-notes]');
      if (notes) notes.value = item.properties?.['Customisation notes'] || '';
      component.querySelectorAll('[data-pet-photo]').forEach(input => {
        const value = item.properties?.[propertyName(input)];
        if (value && safePhoto(value)) component.retainPhoto(input, value);
      });
      // Customer reviews consent again, including rights to any replacement photo.
      component.querySelectorAll('[data-pet-consents] input').forEach(input => { input.checked = false; });
      component.updateVariant();
    }
    async load() {
      try {
        if (!navigator.locks) throw new Error(message(this.component, 'editBrowserRequired'));
        const cart = await this.cart();
        const pending = cart.items.filter(item => item.properties?._pet_edit_pending === 'true' && item.properties._pet_edit_source === this.key);
        if (pending.length > 1) throw new Error(message(this.component, 'editMultiplePending'));
        if (pending.length === 1) {
          const item = pending[0], properties = item.properties;
          if (!this.record || this.record.token !== properties._pet_edit_id) {
            this.record = { token: properties._pet_edit_id, fingerprint: properties._pet_edit_fingerprint, variant: properties._pet_edit_target_variant, quantity: Number(properties._pet_edit_target_quantity), petCount: properties._pet_edit_target_pet_count, stage: 'added' };
            this.persist();
          }
          this.fill(item);
          this.show(this.component.dataset.editReceived, { recover: true, discard: Boolean(await this.findOriginal(cart)) });
          return;
        }
        if (this.record) {
          this.show(message(this.component, 'editPreviousSave'), { recover: true });
          return;
        }
        const item = cart.items.find(item => item.key === this.key);
        if (!item) throw new Error(message(this.component, 'editNotInBag'));
        this.original = item;
        this.fingerprint = await signature(item);
        this.fill(item);
        this.show(this.component.dataset.editSavedChoices, { ready: true });
      } catch (error) {
        this.show(error.message, { recover: !this.original });
      }
    }
    async findOriginal(cart) {
      const fingerprint = this.record?.fingerprint || this.fingerprint;
      const exact = cart.items.find(item => item.key === this.key);
      if (exact) {
        if (await signature(exact) !== fingerprint) throw new Error(message(this.component, 'editOtherWindow'));
        return exact;
      }
      const matches = [];
      for (const item of cart.items) if (await signature(item) === fingerprint) matches.push(item);
      if (matches.length > 1) throw new Error(message(this.component, 'editCannotIdentify'));
      return matches[0];
    }
    data() {
      const data = new FormData(this.form);
      data.delete('return_to');
      // Keep unedited supported properties, without reviving inactive pet fields.
      const managed = new Set(Array.from(this.form.elements).map(propertyName).filter(Boolean));
      for (const [name, value] of Object.entries(this.original.properties || {})) {
        if (!managed.has(name) && !name.startsWith('_pet_edit_')) data.set(`properties[${name}]`, String(value));
      }
      if (this.original.properties?._customisation_id) data.set('properties[_customisation_id]', this.original.properties._customisation_id);
      this.component.querySelectorAll('[data-pet-photo]').forEach(input => {
        data.delete(input.name);
        if (input.matches(':disabled')) return;
        if (input.files.length) data.set(input.name, input.files[0]);
        else if (this.component.retainedPhotos.has(input)) data.set(input.name, this.component.retainedPhotos.get(input));
      });
      return data;
    }
    async exclusive(action) {
      if (!navigator.locks) return;
      return navigator.locks.request('pet-reverie-cart-edit', { ifAvailable: true }, async lock => {
        if (!lock) { this.show(message(this.component, 'editAnotherSaving'), { recover: true }); return; }
        await action();
      });
    }
    save() { return this.exclusive(() => this.performSave()); }
    async performSave() {
      // Capture files before disabling the fieldset. The native submit is prevented.
      const data = this.data();
      this.busy(message(this.component, 'editSaving'));
      try {
        const original = await this.findOriginal(await this.cart());
        if (!original) throw new Error(message(this.component, 'editOriginalMissing'));
        const direct = String(data.get('id')) === String(original.variant_id || original.id) && !Array.from(data.values()).some(value => value instanceof File && value.size);
        this.record = {
          token: crypto.randomUUID(), fingerprint: this.fingerprint, variant: data.get('id'),
          quantity: Number(data.get('quantity')), petCount: data.get('properties[Pet count]'), stage: 'adding', direct
        };
        this.persist();
        const metadata = {
          _pet_edit_id: this.record.token, _pet_edit_pending: 'true', _pet_edit_source: this.key,
          _pet_edit_fingerprint: this.fingerprint, _pet_edit_target_variant: this.record.variant,
          _pet_edit_target_quantity: this.record.quantity,
          ...(this.record.petCount ? { _pet_edit_target_pet_count: this.record.petCount } : {})
        };
        Object.entries(metadata).forEach(([key, value]) => data.set(`properties[${key}]`, String(value)));
        let response;
        try {
          if (direct) {
            const properties = {};
            for (const [name, value] of data.entries()) {
              const key = /^properties\[(.+)\]$/.exec(name)?.[1];
              if (key && !editFields.includes(key) && typeof value === 'string' && value.trim()) properties[key] = value;
            }
            response = await this.write('change', { id: original.key, quantity: this.record.quantity, properties });
          } else response = await this.write('add', data);
        } catch { /* Reconcile an uncertain write; never re-add. */ }
        const cart = await this.cart();
        const found = cart.items.some(item => item.properties?._pet_edit_id === this.record.token);
        if (!found && response && [400, 404, 422].includes(response.status)) {
          this.record = null; this.persist();
          this.show(message(this.component, 'editSaveFailed'), { ready: true });
          return;
        }
        await this.finish(cart);
      } catch (error) {
        this.show(error.message || message(this.component, 'editSaveStatusUnconfirmed'), { recover: Boolean(this.record), keep: error.allowKeep, discard: error.allowDiscard });
      }
    }
    check(keep = false) {
      return this.exclusive(async () => {
        if (!this.record) { await this.load(); return; }
        this.busy(message(this.component, 'editChecking'));
        if (keep) { this.record.stage = 'finalizing'; this.persist(); }
        try { await this.finish(await this.cart()); }
        catch (error) { this.show(error.message || message(this.component, 'editSaveCheckFailed'), { recover: true, keep: error.allowKeep, discard: error.allowDiscard }); }
      });
    }
    discardChanges() {
      return this.exclusive(async () => {
        this.busy(message(this.component, 'editDiscarding'));
        try {
          const cart = await this.cart();
          const original = await this.findOriginal(cart);
          const matches = cart.items.filter(item => item.properties?._pet_edit_id === this.record?.token && item.properties._pet_edit_pending === 'true');
          if (!original || matches.length !== 1) throw new Error(message(this.component, 'editOriginalNotConfirmed'));
          try { await this.write('change', { id: matches[0].key, quantity: 0 }); } catch { /* Confirm removal below. */ }
          const updated = await this.cart();
          if (updated.items.some(item => item.properties?._pet_edit_id === this.record.token)) throw new Error(message(this.component, 'editPendingNotRemoved'));
          this.record = null; this.persist();
          location.assign(this.cartUrl);
        } catch (error) { this.show(error.message, { recover: true }); }
      });
    }
    async finish(cart) {
      let matches = cart.items.filter(item => item.properties?._pet_edit_id === this.record.token);
      if (matches.length !== 1) throw new Error(message(this.component, 'editSaveUnconfirmed'));
      let replacement = matches[0];
      if (String(replacement.variant_id || replacement.id) !== String(this.record.variant) || replacement.quantity !== this.record.quantity) {
        throw Object.assign(new Error(message(this.component, 'editNotFull')), { allowDiscard: Boolean(await this.findOriginal(cart)) });
      }
      const option = Array.from(this.component.variant.options).find(option => option.value === String(this.record.variant));
      const properties = replacement.properties || {};
      const chosenCount = String(properties['Pet count'] || '');
      const selectableCount = option?.dataset.petCountChoice === 'true';
      const count = selectableCount ? (['1', '2'].includes(chosenCount) ? Number(chosenCount) : 0) : Number(option?.dataset.petCount || 0);
      if (selectableCount && this.record.petCount && chosenCount !== String(this.record.petCount)) throw new Error(this.component.dataset.editDetailsError);
      const hasValue = key => typeof properties[key] === 'string' && properties[key].trim().length > 0;
      const deferred = properties['Photo submission'] === 'Send after checkout';
      const complete = option?.dataset.petProfile === 'portrait' && count > 0 && hasValue('Custom order confirmation') && hasValue('Policy version') && (deferred
        ? properties['Customisation status'] === 'Awaiting photos and details'
        : (!properties['Photo submission'] || properties['Photo submission'] === 'Upload now') && hasValue('Photo rights confirmation') && (count !== 1 || hasValue('Pet name')) && photoKeys.slice(0, count).every(key => safePhoto(properties[key])));
      if (!complete) throw new Error(this.component.dataset.editDetailsError);
      if (this.record.direct) {
        this.record = null; this.persist();
        location.assign(`${this.cartUrl}?edited=1`);
        return;
      }
      let original = await this.findOriginal(cart);
      if (original) {
        if (replacement.properties._pet_edit_pending !== 'true') throw new Error(message(this.component, 'editNeedsReview'));
        this.record.stage = 'removing'; this.persist();
        try { await this.write('change', { id: original.key, quantity: 0 }); } catch { /* Read back before deciding whether removal failed. */ }
        cart = await this.cart();
        original = await this.findOriginal(cart);
        if (original) throw Object.assign(new Error(message(this.component, 'editOriginalReplace')), { allowDiscard: true });
      } else if (!['removing', 'finalizing'].includes(this.record.stage) && replacement.properties._pet_edit_pending === 'true') {
        throw Object.assign(new Error(message(this.component, 'editOriginalGone')), { allowKeep: true });
      }
      replacement = cart.items.find(item => item.properties?._pet_edit_id === this.record.token);
      if (!replacement) throw new Error(message(this.component, 'editEditedNotFound'));
      if (replacement.properties._pet_edit_pending === 'true') {
        this.record.stage = 'finalizing'; this.persist();
        const properties = { ...replacement.properties };
        editFields.forEach(key => delete properties[key]);
        try { await this.write('change', { id: replacement.key, quantity: replacement.quantity, properties }); } catch { /* Reconcile property/key changes as well. */ }
        cart = await this.cart();
        replacement = cart.items.find(item => item.properties?._pet_edit_id === this.record.token);
        if (!replacement || replacement.properties._pet_edit_pending === 'true') throw new Error(message(this.component, 'editSavedCheckAgain'));
      }
      this.record = null; this.persist();
      location.assign(`${this.cartUrl}?edited=1`);
    }
  }
  const initialize = async () => {
    await customElements.whenDefined('pet-studio-customization');
    document.querySelectorAll('pet-studio-customization').forEach(component => {
      if (component.editRequested && !component.cartEditor) new PetCartEditor(component);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
